import { CourseCollapseItem } from 'app/imports/App.components'
import { NumberBadge } from 'components/number-badge/NumberBadge'
import { useCollapseContext } from 'features/UI'
import { useEffectOnce } from 'hooks/useEffectOnce'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from 'store/hooks'
import { selectIsFaqOpen } from 'store/slice/learn-library.slice'
import { ArticleType, ETimelinePostStatus } from 'types'
import { LibraryArticleItem } from './LibraryArticleItem'

export function LibraryCategoriesItem({
  id,
  title,
  icon,
  articles,
  isPinned,
  isFirstPinned,
}: {
  id: number
  title: string
  icon: string | null
  isFirstPinned: boolean
  isPinned: boolean
  articles: {
    id: number
    name: string
    post: string
    status: ETimelinePostStatus
    icon: string | null
    silentReadingTime?: number
  }[]
}) {
  const dispatch = useAppDispatch()
  const { setActiveCollapse } = useCollapseContext()
  const { learnLibraryItemType, learnLibraryItemId } = useParams()
  const isFaqOpen = useSelector(selectIsFaqOpen)

  const reviewItemsCount = useMemo(() => {
    const reviewArticles = articles.filter(
      (article) => article.status === ETimelinePostStatus.review,
    )
    return reviewArticles.length
  }, [articles])

  useEffectOnce(() => {
    const isTypeCorrect = learnLibraryItemType === ArticleType.LearnLibrary
    const childIds = articles.map((article) => article.id)
    const hasChild = childIds.includes(Number(learnLibraryItemId))
    if (isTypeCorrect && hasChild) {
      setActiveCollapse(id)
    }
  })

  useEffect(() => {
    if (isFaqOpen && isFirstPinned) {
      setActiveCollapse(id)
    }
  }, [dispatch, id, isFaqOpen, isFirstPinned, setActiveCollapse])

  return (
    <CourseCollapseItem
      id={id}
      name={
        <>
          {title}
          {reviewItemsCount > 0 && <NumberBadge value={reviewItemsCount} />}
        </>
      }
      bannerUrl={icon}
      isForceExpanded={false}
    >
      {articles.map((article) => {
        return (
          <LibraryArticleItem
            key={article.id}
            id={article.id}
            title={article.post || article.name}
            status={article.status}
            icon={article.icon}
            silentReadingTime={article.silentReadingTime}
            isPinned={isPinned}
          />
        )
      })}
    </CourseCollapseItem>
  )
}
